import { useAuth } from '../context/AuthContext';
import { AuthUser } from '../types/api';

/**
 * Hook personalizado para acessar dados do usuário da sessão
 * Fornece métodos convenientes para obter informações do usuário logado
 */
export const useSessionUser = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  /**
   * Retorna o ID do usuário logado
   * @returns string | null - ID do usuário ou null se não estiver logado
   */
  const getUserId = (): string | null => {
    return user?.id || null;
  };

  /**
   * Retorna o nome do usuário logado
   * @returns string | null - Nome do usuário ou null se não estiver logado
   */
  const getUserName = (): string | null => {
    const u = user;
    if (!u) return null;
    return (u.nome?.trim() || u.name || u.username || null)?.trim() || null;
  };

  /**
   * Retorna o email do usuário logado
   * @returns string | null - Email do usuário ou null se não estiver logado
   */
  const getUserEmail = (): string | null => {
    return user?.email || null;
  };

  /**
   * Retorna o username do usuário logado
   * @returns string | null - Username do usuário ou null se não estiver logado
   */
  const getUsername = (): string | null => {
    return user?.username || null;
  };

  /**
   * Retorna as roles do usuário logado
   * @returns string[] - Array de roles ou array vazio se não estiver logado
   */
  const getUserRoles = (): string[] => {
    return user?.roles || [];
  };

  /**
   * Verifica se o usuário tem uma role específica
   * @param role - Role a ser verificada
   * @returns boolean - true se o usuário tem a role, false caso contrário
   */
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || false;
  };

  /**
   * Verifica se o usuário tem pelo menos uma das roles fornecidas
   * @param roles - Array de roles a serem verificadas
   * @returns boolean - true se o usuário tem pelo menos uma das roles, false caso contrário
   */
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => hasRole(role));
  };

  /**
   * Retorna se o usuário está ativo
   * @returns boolean - true se o usuário está ativo, false caso contrário
   */
  const isUserActive = (): boolean => {
    return user?.isActive || false;
  };

  /**
   * Retorna dados completos do usuário
   * @returns AuthUser | null - Dados completos do usuário ou null se não estiver logado
   */
  const getUserData = (): AuthUser | null => {
    return user;
  };

  /**
   * Retorna informações resumidas do usuário para logs
   * @returns object - Objeto com informações básicas do usuário
   */
  const getUserInfo = () => {
    const display = user ? (user.nome?.trim() || user.name || user.username || 'N/A') : 'N/A';
    return {
      id: user?.id || 'N/A',
      name: display,
      email: user?.email || 'N/A',
      username: user?.username || 'N/A',
      roles: user?.roles || [],
      isActive: user?.isActive || false,
      isAuthenticated,
      isLoading
    };
  };

  return {
    // Dados do usuário
    user,
    isAuthenticated,
    isLoading,
    
    // Métodos para obter dados específicos
    getUserId,
    getUserName,
    getUserEmail,
    getUsername,
    getUserRoles,
    getUserData,
    getUserInfo,
    
    // Métodos para verificar permissões
    hasRole,
    hasAnyRole,
    isUserActive
  };
};

